import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { checkAuthenticationStatus } from '../../services/authService';
import LoadingIndicator from '../LoadingIndicator';

/**
 * ProtectedRoute component
 * Implements SAML authentication flow:
 * 1. Checks authentication via API call to /api/Login/check-auth-status
 * 2. If authenticated, renders the protected component
 * 3. If not authenticated, redirects to SAML login with current URL preserved
 * 
 * @param {Object} props - Component props
 * @param {Component} props.element - React component to render if authenticated
 * @returns {Component} Protected component or redirect
 */
const ProtectedRoute = ({ element: Element }) => {
    const { isAuthenticated, loading, refreshAuth } = useAuth();
    const location = useLocation();
    const [authCheckLoading, setAuthCheckLoading] = useState(true);
    const [apiAuthStatus, setApiAuthStatus] = useState(null);

    // Perform API-based authentication check on component mount
    useEffect(() => {
        const performAuthCheck = async () => {
            try {
                const authResult = await checkAuthenticationStatus();
                
                setApiAuthStatus(authResult.isAuthenticated);
                
                // If authentication status changed, refresh the auth context
                if (authResult.isAuthenticated !== isAuthenticated) {
                    await refreshAuth();
                }
            } catch (error) {
                console.error('ProtectedRoute: Auth check failed:', error);
                setApiAuthStatus(false);
            } finally {
                setAuthCheckLoading(false);
            }
        };

        performAuthCheck();
    }, [location.pathname, isAuthenticated, refreshAuth]);

    // Show loading indicator while checking authentication
    if (loading || authCheckLoading) {
        return <LoadingIndicator message="Checking authentication..." />;
    }

    // Use API auth status if available, otherwise fall back to context
    const userIsAuthenticated = apiAuthStatus !== null ? apiAuthStatus : isAuthenticated;

    if (!userIsAuthenticated) {
        // Preserve the attempted URL (including search params) for redirect after login
        const loginPath = `/${location.search}`;
        return <Navigate to={loginPath} replace />;
    }

    // If authenticated, render the protected component
    return Element;
};

export default ProtectedRoute;